import { RequestPromise, rawGet, rawPatch, rawPost, rawPut } from '@portal-packages/api';
import T from 'ramda/es/T';

import { API_CLIENTS } from '@constants/api';
import { ResendDeveloperConfirmationEmailMutation } from 'modules/SignIn/types';
import { splitFileFromFormData } from 'utils/form';
import { propData } from 'utils/ramda';

import {
  CHANGE_DEVELOPER_PASSWORD,
  CREATE_DEVELOPER,
  DEVELOPER,
  RESEND_DEVELOPER_CONFIRMATION_EMAIL,
  RESET_DEVELOPER_PASSWORD,
  UPDATE_DEVELOPER,
} from '../constants';
import { Developer, DeveloperUpdate, NewDeveloper } from '../types';

export const getDeveloper = (developerUUID: string): Promise<Developer> =>
  rawGet<Developer>({
    client: API_CLIENTS.APP,
    entity: DEVELOPER,
    url: `/v1/developers/${developerUUID}`,
  }).then(propData);

type UpdateDeveloperMutation = {
  developerUUID: string;
  data: DeveloperUpdate;
};

export const updateDeveloper = ({ developerUUID, data }: UpdateDeveloperMutation): RequestPromise<void> => {
  const formData = splitFileFromFormData(data, 'photo', 'developer');

  return rawPut<void, FormData>({
    client: API_CLIENTS.APP,
    entity: UPDATE_DEVELOPER,
    url: `/v1/developers/${developerUUID}`,
    shouldNotifySuccess: T,
    data: formData,
    config: {
      headers: {
        'content-type': 'multipart/form-data',
      },
    },
  });
};

export type CreateDeveloperMutation = {
  acceptTerms: boolean;
  developer: NewDeveloper;
};

export const createDeveloper = ({ acceptTerms, developer }: CreateDeveloperMutation): RequestPromise<void> =>
  rawPost({
    client: API_CLIENTS.APP,
    entity: CREATE_DEVELOPER,
    url: '/v1/developers',
    config: {
      params: {
        acceptTerms,
      },
    },
    data: developer,
    requiresAuthentication: false,
  });

type ChangeDeveloperPasswordMutation = {
  newPassword: string;
  currentPassword: string;
  developerID: string;
};

export const changeDeveloperPassword = ({
  newPassword,
  currentPassword,
  developerID,
}: ChangeDeveloperPasswordMutation): RequestPromise<void> =>
  rawPatch({
    client: API_CLIENTS.APP,
    entity: CHANGE_DEVELOPER_PASSWORD,
    url: `/v1/developers/${developerID}/actions/change-password`,
    data: {
      newPassword,
      currentPassword,
    },
  });

type ResetDeveloperPasswordMutation = {
  developerEmail: string;
};

export const resetDeveloperPassword = ({ developerEmail }: ResetDeveloperPasswordMutation): RequestPromise<void> =>
  rawPut({
    client: API_CLIENTS.APP,
    entity: RESET_DEVELOPER_PASSWORD,
    url: '/actions/reset-password',
    config: {
      params: {
        developerEmail,
      },
    },
    requiresAuthentication: false,
  });

export const resendDeveloperConfirmationEmail = ({
  developerEmail,
}: ResendDeveloperConfirmationEmailMutation): RequestPromise<void> =>
  rawPut({
    client: API_CLIENTS.APP,
    entity: RESEND_DEVELOPER_CONFIRMATION_EMAIL,
    url: '/actions/verify-email',
    config: {
      params: {
        developerEmail,
      },
    },
    requiresAuthentication: false,
  });
